import { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { 
  PRACTICE_AREAS, PRACTICE_CATEGORIES, INSURANCE_SPECIALTIES,
  getPracticeAreasByCategory 
} from "@/constants/legalSubjects";

export const PracticeAreaSelector = ({ 
  value, 
  onChange, 
  category = null,
  placeholder = "Select Practice Area",
  showCategory = true,
  className = ""
}) => {
  const areas = category ? getPracticeAreasByCategory(category) : PRACTICE_AREAS;

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={className} data-testid="practice-area-selector">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-80">
        {showCategory && !category ? (
          PRACTICE_CATEGORIES.map(cat => {
            const catAreas = getPracticeAreasByCategory(cat.value);
            if (catAreas.length === 0) return null;
            return (
              <div key={cat.value}>
                <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">
                  {cat.label}
                </div>
                {catAreas.map(area => (
                  <SelectItem key={area.value} value={area.value}>{area.label}</SelectItem>
                ))}
              </div>
            );
          })
        ) : (
          areas.map(area => (
            <SelectItem key={area.value} value={area.value}>{area.label}</SelectItem>
          ))
        )}
      </SelectContent>
    </Select>
  );
};

export const CategorySelector = ({ value, onChange, className = "" }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className={className} data-testid="category-selector">
      <SelectValue placeholder="Select Category" />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="all">All Categories</SelectItem>
      {PRACTICE_CATEGORIES.map(cat => (
        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

export const InsuranceSpecialtySelector = ({ value, onChange, className = "" }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className={className} data-testid="insurance-specialty-selector">
      <SelectValue placeholder="Select Insurance Specialty" />
    </SelectTrigger>
    <SelectContent>
      {INSURANCE_SPECIALTIES.map(spec => (
        <SelectItem key={spec.value} value={spec.value}>
          <div>
            <span>{spec.label}</span>
          </div>
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

export const MultiPracticeAreaSelector = ({ selected = [], onChange }) => {
  const [categoryFilter, setCategoryFilter] = useState("all");
  const areas = categoryFilter === "all" ? PRACTICE_AREAS : getPracticeAreasByCategory(categoryFilter);

  const toggleArea = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter(s => s !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <div className="space-y-3" data-testid="multi-practice-area-selector">
      <CategorySelector value={categoryFilter} onChange={setCategoryFilter} />
      <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto p-2 border rounded-lg bg-white">
        {areas.map(area => (
          <Badge
            key={area.value}
            variant={selected.includes(area.value) ? "default" : "outline"}
            className={`cursor-pointer text-xs transition-all ${
              selected.includes(area.value) 
                ? "bg-blue-600 text-white hover:bg-blue-700" 
                : "hover:bg-blue-50 hover:border-blue-300"
            }`}
            onClick={() => toggleArea(area.value)}
          >
            {area.label}
          </Badge>
        ))}
      </div>
      {selected.length > 0 && (
        <p className="text-xs text-slate-500">{selected.length} selected</p>
      )}
    </div>
  );
};
